import { useState, useEffect, useRef } from "react"
import { Search, MapPin,  Code, Briefcase, ChevronDown, Filter, ChevronRight } from "lucide-react"
import ReactCountryFlag from "react-country-flag"
import TalentDetails from "./components/TalentDetails"
import Logo from '../../src/assets/images/Logo.svg'
import Profile from '../../src/assets/images/profile.png'
import ThreeDots from '../../src/assets/images/dots-three.svg'
import P1 from '../../src/assets/images/p1.svg'
import P2 from '../../src/assets/images/p2.svg'
import P3 from '../../src/assets/images/p3.svg'
import P4 from '../../src/assets/images/p4.svg'
import P5 from '../../src/assets/images/p5.svg'
import Bell from '../../src/assets/images/bell.svg'
import DropDown from '../../src/assets/images/dropdown.svg'
import LocationIcon from '../../src/assets/images/map.svg'
import User from "../../src/assets/images/user.svg"
import { LuChevronDown } from "react-icons/lu";
import Modal from "../components/Modal/Modal";

import Company from '../../src/assets/images/buildingsmini.svg'
import Setting from '../../src/assets/images/gear-six.svg'
import BagSimple from '../../src/assets/images/bag-simple.svg'
import Sorting from '../../src/assets/images/sort-ascending.svg'
import ExportIcon from '../../src/assets/images/export.svg'
import Linkedin from "../../src/assets/images/linkedin.svg"
import Github from '../..//src/assets/images/github.svg'
import BuildingIcon from '../../src/assets/images/buildings.svg'
import Dropdown from '../../src/assets/images/caret-down.svg'
import ArrowRight from '../../src/assets/images/arrow-right.svg'
import ArrowLeft from '../../src/assets/images/arrow-left.svg'
import toast from "react-hot-toast"
import apiEndpoints from "../components/constants/apiEndpoints"
import { api } from "../api"
import FilterDropdown from "./components/FilterDropdown"
import { useNavigate } from "react-router-dom"
import { FaSortAlphaDown, FaSortAlphaUp } from "react-icons/fa"
import { useAuth } from "../components/auth/authProvider"

// Country name to code mapping
const countryToCode = {
  "United States": "US",
  "Canada": "CA",
  "United Kingdom": "GB",
  "Australia": "AU",
  "Germany": "DE",
  "France": "FR",
  "India": "IN",
  "China": "CN",
  "Japan": "JP",
  "Brazil": "BR",
}

function getCountryCode(countryName) {
  return countryToCode[countryName] || 'US' // Default to US if country code not found
}

// Move this outside the component to avoid recreation on re-renders
const profileImages = [P1, P2, P3, P4, P5];
const profileImageMap = new Map();

function getProfileImage(id) {
  if (!profileImageMap.has(id)) {
    const randomImage = profileImages[Math.floor(Math.random() * profileImages.length)];
    profileImageMap.set(id, randomImage);
  }
  return profileImageMap.get(id);
}

function DashboardNew() {
  // API State
  const [query, setQuery] = useState("")
  const [locations, setLocations] = useState([])
  const [companies, setCompanies] = useState([])
  const [skills, setSkills] = useState([])
  const [selectedLocation, setSelectedLocation] = useState([])
  const [selectedCompany, setSelectedCompany] = useState([])
  const [selectedSkill, setSelectedSkill] = useState([])
  const [results, setResults] = useState([])
  const [page, setPage] = useState(1)
  const [maxPage, setMaxPage] = useState(1)
  const [totalResults, setTotalResults] = useState(0)
  const [isSidebarOpen, setIsSidebarOpen] = useState(false)
  const [selectedResult, setSelectedResult] = useState(null)
  const [openLocationFilter, setOpenLocationFilter] = useState(false)
  const [openCompanyFilter, setOpenCompanyFilter] = useState(false)
  const [openSkillFilter, setOpenSkillFilter] = useState(false)
  const [openSortDropdown, setOpenSortDropdown] = useState(false)
  const [sortBy, setSortBy] = useState(null)
  const [sortColumn, setSortColumn] = useState(null)
  const [openProfileDropdown, setOpenProfileDropdown] = useState(false)
  const [perPage, setPerPage] = useState(20)
  const [openPerPageDropdown, setOpenPerPageDropdown] = useState(false)
  // Add new state variables for folder functionality
  const [checkedResults, setCheckedResults] = useState([])
  const [isFolderModalOpen, setIsFolderModalOpen] = useState(false)
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [selectedTab, setSelectedTab] = useState(0)
  const [selectedFolderDropdown, setSelectedFolderDropdown] = useState(null)
  const [openFolderDropdown, setOpenFolderDropdown] = useState(false)
  const [folders, setFolders] = useState([])
  const [newFolder, setNewFolder] = useState('')
  const [editFolder, setEditFolder] = useState(null)
  const [editType, setEditType] = useState(null)
  const folderRef = useRef(null)
  
  const navigate = useNavigate()
  const perPageRef = useRef(null)
  const { isLoggedIn, logout, user } = useAuth();
  const [userData, setUserData] = useState(null);

  useEffect(() => {
    // Get user data from localStorage
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      setUserData(JSON.parse(storedUser));
    }
  }, []);

  // API Functions
  function generateSearchParams(filters) {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, values]) => {
      if (Array.isArray(values)) {
        // For arrays, append each value separately
        if (values.length > 0) {
          values.forEach(value => {
            if (value) params.append(key, value);
          });
        }
      } else if (values) {
        params.append(key, values);
      }
    });
    if (sortColumn && sortBy) {
      params.append('sort', sortBy);
      params.append('sort_by', sortColumn);
    }
    params.append('page_size', perPage);
    return params.toString();
  }

  const handleFetchSearchFilters = async () => {
    try {
      const response = await api.get(apiEndpoints.dashboard.getSeachFilters)
      setLocations(
        response.data.data.location.sort((a, b) => {
          if (a.key === "United States") return -1;
          if (b.key === "United States") return 1;
          return a.key.localeCompare(b.key);
        })
      )
      setCompanies(response.data.data.company.sort((a, b) => a.key.localeCompare(b.key)))
      setSkills(response.data.data.skills.sort((a, b) => a.key.localeCompare(b.key)))
    } catch (error) {
      toast.error("Failed to fetch search filters")
    }
  }

  const handleFetchResults = async () => {
    try {
      const params = generateSearchParams({ location: selectedLocation, skills: selectedSkill, company: selectedCompany, q: query, page: 1 })
      const response = await api.get(`${apiEndpoints.dashboard.getSeachResults}?${params}`)
      setResults(response.data.data)
      setPage(response.data.pagination.page)
      setMaxPage(response.data.pagination.max_page)
      setTotalResults(response.data.pagination.total_records)
    } catch (error) {
      toast.error("Failed to fetch search results")
    }
  }

  const handleSearch = async () => {
    const params = generateSearchParams({ location: selectedLocation, skills: selectedSkill, company: selectedCompany, q: query, page: 1 })
    try {
      const response = await api.get(`${apiEndpoints.dashboard.getSeachResults}?${params}`);
      setResults(response.data.data)
      setPage(1)
      setMaxPage(response.data.pagination.max_page)
      setTotalResults(response.data.pagination.total_records)
    } catch (error) {
      toast.error("Failed to fetch search results")
    }
  }

  const handleSort = async (column) => {
    let updatedSortBy = "asc";
    let updatedSortColumn = column;

    if (column === sortColumn) {
      if (sortBy === null) updatedSortBy = "asc";
      else if (sortBy === "asc") updatedSortBy = "desc";
      else {
        updatedSortColumn = null;
        updatedSortBy = null;
      }
    }

    setSortBy(updatedSortBy);
    setSortColumn(updatedSortColumn);
    setOpenSortDropdown(false);

    const params = generateSearchParams({ 
      location: selectedLocation, 
      skills: selectedSkill, 
      company: selectedCompany, 
      q: query, 
      page: 1,
      sort: updatedSortBy,
      sort_by: updatedSortColumn 
    });

    try {
      const response = await api.get(`${apiEndpoints.dashboard.getSeachResults}?${params}`);
      setResults(response.data.data);
      setPage(1);
      setMaxPage(response.data.pagination.max_page);
      setTotalResults(response.data.pagination.total_records);
    } catch (error) {
      toast.error("Failed to sort results");
    }
  };
  const handleLogout = async () => {
    try {
      await api.post(apiEndpoints.auth.logout, {}, { withCredentials: true });
      logout();
      // Redirect to Home (if necessary)
      navigate("/"); 
    } catch (error) {
      logout();
      // Redirect to Home (if necessary)
      navigate("/"); 
    }
  };

  const handleChangePage = async (page) => {
    const params = generateSearchParams({ location: selectedLocation, skills: selectedSkill, company: selectedCompany, q: query, page })
    try {
      const response = await api.get(`${apiEndpoints.dashboard.getSeachResults}?${params}`);
      setResults(response.data.data)
      setPage(response.data.pagination.page)
      setMaxPage(response.data.pagination.max_page)
      setTotalResults(response.data.pagination.total_records)
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      toast.error("Failed to fetch search results")
    }
  }

  const handleLocationSelect = (locations) => {
    setSelectedLocation(locations);
    setOpenLocationFilter(false);
    
    // Call API with new state
    const params = generateSearchParams({ 
      location: locations, 
      skills: selectedSkill, 
      company: selectedCompany, 
      q: query, 
      page: 1,
      sort: sortBy,
      sort_by: sortColumn
    });
    
    api.get(`${apiEndpoints.dashboard.getSeachResults}?${params}`)
      .then(response => {
        setResults(response.data.data);
        setPage(1);
        setMaxPage(response.data.pagination.max_page);
        setTotalResults(response.data.pagination.total_records);
      })
      .catch(() => toast.error("Failed to fetch search results"));
  }

  const handleCompanySelect = (companies) => {
    setSelectedCompany(companies);
    setOpenCompanyFilter(false);
    
    // Call API with new state
    const params = generateSearchParams({ 
      location: selectedLocation, 
      skills: selectedSkill, 
      company: companies, 
      q: query, 
      page: 1,
      sort: sortBy,
      sort_by: sortColumn
    });
    
    api.get(`${apiEndpoints.dashboard.getSeachResults}?${params}`)
      .then(response => {
        setResults(response.data.data);
        setPage(1);
        setMaxPage(response.data.pagination.max_page);
        setTotalResults(response.data.pagination.total_records);
      })
      .catch(() => toast.error("Failed to fetch search results"));
  }

  const handleSkillSelect = (skills) => {
    setSelectedSkill(skills);
    setOpenSkillFilter(false);
    
    // Call API with new state
    const params = generateSearchParams({ 
      location: selectedLocation, 
      skills: skills, 
      company: selectedCompany, 
      q: query, 
      page: 1,
      sort: sortBy,
      sort_by: sortColumn
    });
    
    api.get(`${apiEndpoints.dashboard.getSeachResults}?${params}`)
      .then(response => {
        setResults(response.data.data);
        setPage(1);
        setMaxPage(response.data.pagination.max_page);
        setTotalResults(response.data.pagination.total_records);
      })
      .catch(() => toast.error("Failed to fetch search results"));
  }

  const handlePerPageChange = async (value) => {
    const params = generateSearchParams({ 
      location: selectedLocation, 
      skills: selectedSkill, 
      company: selectedCompany, 
      q: query, 
      page: 1,
      sort: sortBy,
      sort_by: sortColumn,
      page_size: value
    });

    try {
      const response = await api.get(`${apiEndpoints.dashboard.getSeachResults}?${params}`);
      setResults(response.data.data);
      setPage(1);
      setMaxPage(response.data.pagination.max_page);
      setTotalResults(response.data.pagination.total_records);
      setPerPage(value);
      setOpenPerPageDropdown(false);
    } catch (error) {
      toast.error("Failed to update results per page");
    }
  };

  // Add functions for handling checked results
  const handleCheckResults = () => {
    if (checkedResults.length === results.length) {
      setCheckedResults([])
    } else {
      setCheckedResults(results.map((result) => result?.id))
    }
  }

  const handleCheckResult = (id, e) => {
    e.stopPropagation(); // Stop event propagation
    if (checkedResults.includes(id)) {
      setCheckedResults(checkedResults.filter((check) => check !== id))
    } else {
      setCheckedResults([...checkedResults, id])
    }
  }

  // Add fetch folders function
  const handleFetchFolders = async () => {
    try {
      const response = await api.get(`${apiEndpoints.dashboard.folder}`)
      setFolders(response.data.folders)
    } catch (error) {
      // toast.error("Failed to fetch folders")
    }
  }

  // Add folder operations
  const addToFolder = async () => {
    if (selectedTab === 1) {
      if (newFolder === '') return toast.error("Please enter folder name")

      try {
        const response = await api.post(`${apiEndpoints.dashboard.folder}`, { folder_name: newFolder })
        await api.post(`${apiEndpoints.dashboard.folderLead(response.data.folder._id)}`, { lead_ids: checkedResults })

        toast.success("Leads added to your new folder!")
        setCheckedResults([])
        setIsFolderModalOpen(false)
        setNewFolder('')
        handleFetchFolders()
      } catch (error) {
        toast.error("Failed to add leads to folder")
      }
    } else if (selectedTab === 0) {
      if (!selectedFolderDropdown) return toast.error("Please select folder")

      try {
        await api.post(`${apiEndpoints.dashboard.folderLead(selectedFolderDropdown._id)}`, { lead_ids: checkedResults })

        toast.success(`Leads added to ${selectedFolderDropdown.folder_name}!`)
        setCheckedResults([])
        setIsFolderModalOpen(false)
        setSelectedFolderDropdown(null)
      } catch (error) {
        toast.error("Failed to add leads to folder")
      }
    }
  }

  const handleDeleteFolder = async () => {
    try {
      await api.delete(`${apiEndpoints.dashboard.folderDetail(editFolder._id)}`)

      toast.success("Folder deleted!")
      setIsEditModalOpen(false)
      handleFetchFolders()
      setEditFolder(null)
    } catch (error) {
      toast.error("Failed to delete folder")
    }
  }

  const handleUpdateFolder = async () => {
    try {
      await api.put(`${apiEndpoints.dashboard.folderDetail(editFolder._id)}`, { folder_name: editFolder.folder_name })

      toast.success("Folder updated!")
      setIsEditModalOpen(false)
      handleFetchFolders()
      setEditFolder(null)
    } catch (error) {
      toast.error("Failed to update folder")
    }
  }

  const handleClickOutside = (event) => {
    if (folderRef.current && !folderRef.current.contains(event.target)) {
      setOpenFolderDropdown(false);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (isFolderModalOpen) {
      setOpenFolderDropdown(false);
      setSelectedTab(0);
      setSelectedFolderDropdown(null);
      setNewFolder('');
    }
  }, [isFolderModalOpen]);

  useEffect(() => {
    handleFetchSearchFilters()
    handleFetchResults()
    handleFetchFolders()
  }, [])

  useEffect(() => {
    function handleClickOutside(event) {
      if (perPageRef.current && !perPageRef.current.contains(event.target)) {
        setOpenPerPageDropdown(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  return (
    <div className="flex min-h-screen bg-[#e8eaec] relative overflow-hidden">
      {/* Main content */}
      <div className="flex-1 px-6 ">
        <div className="">
          {/* Header */}
          <div className="p-4 border-b flex items-center justify-between">
            <div className="flex items-center  bg-[#ffffff] rounded-full w-[136px] h-[56px] p-4">
              <span> <img src={Logo}/></span>
              <span className="font-medium text-base ml-2">TalentXi</span>&nbsp;
            </div>
            <div className="flex justify-center items-center">
              <div className="flex justify-center items-center h-[56px] w-[56px] bg-[#ffffff] rounded-full mr-3">
                <img src={Bell}/>
              </div>
              <div className="flex items-center relative">
                <button
                  className="flex items-center space-x-2 bg-white rounded-full border px-3 py-1.5"
                  onClick={() => setOpenProfileDropdown(!openProfileDropdown)}
                >
                  <div className=" rounded-full bg-gray-200 overflow-hidden">
                    <img src={Profile} alt="User avatar" width={40} height={40} />
                  </div>
                  <div className="text-lg font-medium">
                    <div className="font-medium text-base">{userData?.email?.split('@')[0] || 'N/A'}</div>
                    <div className="text-xs font-normal text-gray-400">{userData?.email || 'N/A'}</div>
                  </div>
                  <img src={Dropdown}/>
                </button>
                {openProfileDropdown && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-10 top-full">
                    <button
                      onClick={() => {
                        localStorage.removeItem('user'); // Clear user data on logout
                        handleLogout();
                      }}
                      className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                      Logout
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Search section */}
          <div className="px-4 pt-1">
            <div className="bg-[#f5f5f5] p-4 rounded-3xl">
              <div className="text-xs uppercase text-gray-500 font-medium mb-2">SEARCH TALENT</div>
              <div className="relative mb-3">
                <input 
                  type="text" 
                  placeholder="AI Engineer" 
                  className="w-full p-5 pl-10 rounded-2xl outline-none focus:ring-0 focus:border-transparent border-none"
                  value={query}
                  onChange={(e) => {
                    const currentValue = e.target.value;
                    setQuery(currentValue);
                    // Use the current value directly in the API call
                    const params = generateSearchParams({ 
                      location: selectedLocation, 
                      skills: selectedSkill, 
                      company: selectedCompany, 
                      q: currentValue, 
                      page: 1,
                      sort: sortBy,
                      sort_by: sortColumn
                    });
                    
                    api.get(`${apiEndpoints.dashboard.getSeachResults}?${params}`)
                      .then(response => {
                        setResults(response.data.data);
                        setPage(1);
                        setMaxPage(response.data.pagination.max_page);
                        setTotalResults(response.data.pagination.total_records);
                      })
                      .catch(() => toast.error("Failed to fetch search results"));
                  }}
                />
                <Search className="absolute left-3 top-6 text-gray-400" size={16} />
              </div>

              {/* Filters */}
              <div className="flex flex-wrap gap-2">
                <div className="relative">
                  <button 
                    className="flex items-center space-x-1 rounded-full px-3 py-1.5 text-sm bg-[#ffffff]"
                    onClick={() => setOpenLocationFilter(!openLocationFilter)}
                  >
                    <img src={LocationIcon}/>
                    <span>Location {selectedLocation.length > 0 && `(${selectedLocation.length})`}</span>
                    {selectedLocation.length > 0 && <span className="text-gray-500">×</span>}
                    <img src={DropDown}/>
                  </button>
                  <FilterDropdown
                    isOpen={openLocationFilter}
                    onClose={() => setOpenLocationFilter(false)}
                    options={locations}
                    selectedValues={selectedLocation}
                    onSelect={handleLocationSelect}
                    title="Select Location"
                  />
                </div>

                <div className="relative">
                  <button 
                    className="flex items-center space-x-1 rounded-full px-3 py-1.5 text-sm bg-[#ffffff]"
                    onClick={() => setOpenCompanyFilter(!openCompanyFilter)}
                  >
                    <img src={BuildingIcon}/>
                    <span>Company {selectedCompany.length > 0 && `(${selectedCompany.length})`}</span>
                    {selectedCompany.length > 0 && <span className="text-gray-500">×</span>}
                    <img src={DropDown}/>
                  </button>
                  <FilterDropdown
                    isOpen={openCompanyFilter}
                    onClose={() => setOpenCompanyFilter(false)}
                    options={companies}
                    selectedValues={selectedCompany}
                    onSelect={handleCompanySelect}
                    title="Select Company"
                  />
                </div>

                <div className="relative">
                  <button 
                    className="flex items-center space-x-1 rounded-full px-3 py-1.5 text-sm bg-[#ffffff]"
                    onClick={() => setOpenSkillFilter(!openSkillFilter)}
                  >
                   <img src={Setting}/>
                    <span>Skills {selectedSkill.length > 0 && `(${selectedSkill.length})`}</span>
                    {selectedSkill.length > 0 && <span className="text-gray-500">×</span>}
                    <img src={DropDown}/>
                  </button>
                  <FilterDropdown
                    isOpen={openSkillFilter}
                    onClose={() => setOpenSkillFilter(false)}
                    options={skills}
                    selectedValues={selectedSkill}
                    onSelect={handleSkillSelect}
                    title="Select Skills"
                  />
                </div>

                <button className="flex items-center space-x-1 rounded-full px-3 py-1.5 text-sm bg-[#ffffff]">
                <img src={BagSimple}/>
                  <span>Job Title</span>
                  <img src={DropDown}/>
                </button>
              </div>
            </div>

            {/* Results count and export */}
            <div className="flex justify-between items-center mb-4 mt-4">
              <div className="text-base font-normal">
                Total Matches: <span className="font-medium text-base">({totalResults})</span>
              </div>
              <div className="flex space-x-2">
                <button 
                  className="flex items-center space-x-1 border rounded-full px-3 py-1.5 text-sm bg-[#ffffff]"
                  onClick={() => setOpenSortDropdown(!openSortDropdown)}
                >
                  <img src={Sorting}/>
                  <span className="mb-1 font-normal text-sm">Sort By</span>
                  <img src={DropDown}/>
                </button>
                {openSortDropdown && (
                  <div style={{zIndex: 1000}} className="absolute mt-10 w-48 bg-white rounded-md shadow-lg z-10">
                    <div className="py-1">
                      <button
                        onClick={() => handleSort('first_name')}
                        className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center justify-between"
                      >
                        <span>Name</span>
                        {sortColumn === 'first_name' && (
                          sortBy === 'asc' ? <FaSortAlphaDown /> : <FaSortAlphaUp />
                        )}
                      </button>
                      <button
                        onClick={() => handleSort('employer')}
                        className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center justify-between"
                      >
                        <span>Company</span>
                        {sortColumn === 'employer' && (
                          sortBy === 'asc' ? <FaSortAlphaDown /> : <FaSortAlphaUp />
                        )}
                      </button>
                      <button
                        onClick={() => handleSort('title')}
                        className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center justify-between"
                      >
                        <span>Title</span>
                        {sortColumn === 'title' && (
                          sortBy === 'asc' ? <FaSortAlphaDown /> : <FaSortAlphaUp />
                        )}
                      </button>
                      <button
                        onClick={() => handleSort('work_cities')}
                        className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center justify-between"
                      >
                        <span>City</span>
                        {sortColumn === 'work_cities' && (
                          sortBy === 'asc' ? <FaSortAlphaDown /> : <FaSortAlphaUp />
                        )}
                      </button>
                      <button
                        onClick={() => handleSort('state')}
                        className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center justify-between"
                      >
                        <span>State</span>
                        {sortColumn === 'state' && (
                          sortBy === 'asc' ? <FaSortAlphaDown /> : <FaSortAlphaUp />
                        )}
                      </button>
                      <button
                        onClick={() => handleSort('country')}
                        className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center justify-between"
                      >
                        <span>Country</span>
                        {sortColumn === 'country' && (
                          sortBy === 'asc' ? <FaSortAlphaDown /> : <FaSortAlphaUp />
                        )}
                      </button>
                    </div>
                  </div>
                )}
                <button 
                  className="flex items-center space-x-2 border rounded-full px-3 py-1.5 text-sm bg-[#ffffff]"
                  onClick={handleCheckResults}
                >
                  <input 
                    type="checkbox"
                    className="h-4 w-4"
                    checked={checkedResults.length === results.length && results.length > 0}
                    onChange={handleCheckResults}
                  />
                  <span className="font-normal text-sm">Select All</span>
                </button>
                <button 
                  className="flex items-center space-x-1 bg-[#8d50fe] text-white rounded-full px-3 text-sm"
                  onClick={() => {
                    if (checkedResults.length === 0) {
                      toast.error("Please select at least one talent");
                      return;
                    }
                    setIsFolderModalOpen(true);
                  }}
                >
                  <img className="mb-1" src={ExportIcon}/>
                  <span className="mb-1 font-medium text-sm">Export {checkedResults.length > 0 && `(${checkedResults.length})`}</span>
                </button>
              </div>
            </div>

            {/* Talent cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2">
              {results.map((result, index) => (
                <TalentCard
                  key={result.id}
                  id={result.id}
                  name={`${result.first_name} ${result.last_name}`}
                  title={result.title}
                  company={result.employer}
                  location={`${result.work_cities?.[0] || ''}, ${result.state || ''}, ${result.country?.[0] || ''}`}
                  flagCode={result.country?.[0] || 'United States'}
                  linkedin={result.linkedin}
                  github={result.github}
                  isChecked={checkedResults.includes(result.id)}
                  onCheck={handleCheckResult}
                  onClick={() => {
                    // Get the profile image for this talent
                    const profileImage = getProfileImage(result.id);
                    // Set the profile image along with the selected result
                    result.profileImage = profileImage;
                    setSelectedResult(result);
                    setIsSidebarOpen(true);
                  }}
                />
              ))}
            </div>

            {/* Pagination */}
      <div className="flex items-center justify-between mt-6 mb-6">
  {/* Left: Per Page Dropdown */}
  <div className="flex items-center gap-3">
    <div className="relative" ref={perPageRef}>
      <button 
        className="flex items-center space-x-1 border rounded-full px-4 py-2 text-sm bg-white"
        onClick={(e) => {
          e.stopPropagation();
          setOpenPerPageDropdown(!openPerPageDropdown);
        }}
      >
        <span>Show: {perPage}</span>
        <img src={Dropdown}/>
      </button>
      {openPerPageDropdown && (
        <div className="absolute left-0 bottom-full mb-1 w-24 bg-white rounded-md shadow-lg z-50">
          {[16, 20, 40, 60, 80, 100].map((value) => (
            <button
              key={value}
              className={`block w-full text-left px-4 py-2 text-base ${
                perPage === value ? 'bg-gray-100' : 'hover:bg-gray-50'
              }`}
              onClick={(e) => {
                e.stopPropagation();
                handlePerPageChange(value);
              }}
            >
              {value}
            </button>
          ))}
        </div>
      )}
    </div>
    <span className="text-base text-gray-400">
      Results {(page - 1) * perPage + 1} - {Math.min(page * perPage, totalResults)} of {totalResults}
    </span>
  </div>

  {/* Center: Page Numbers */}
  <div className="flex items-center gap-2">
    {maxPage <= 7 ? (
      // If total pages are 7 or less, show all
      Array.from({ length: maxPage }, (_, i) => i + 1).map((pageNum) => (
        <button
          key={pageNum}
          className={`w-8 h-8 flex items-center justify-center border rounded-full ${
            pageNum === page ? 'bg-[#8d50fe] text-white' : 'bg-white'
          }`}
          onClick={() => handleChangePage(pageNum)}
        >
          {pageNum}
        </button>
      ))
    ) : (
      // Dynamic pagination with ellipsis
      <>
        {[1, 2, 3].map((pageNum) => (
          <button
            key={pageNum}
            className={`w-[40px] h-[40px] flex items-center justify-center border rounded-full ${
              pageNum === page ? 'bg-[#ffffff] text-[rgba(0, 0, 0, 1)]' : 'bg-[#f5f5f5]'
            }`}
            onClick={() => handleChangePage(pageNum)}
          >
            {pageNum}
          </button>
        ))}
        <span className="text-gray-400">...</span>
        {[maxPage - 2, maxPage - 1, maxPage].map((pageNum) => (
          <button
            key={pageNum}
            className={`w-[40px] h-[40px] flex items-center justify-center border rounded-full ${
              pageNum === page ? 'bg-[#ffffff] text-[rgba(0, 0, 0, 1)]' : 'bg-[#f5f5f5]'
            }`}
            onClick={() => handleChangePage(pageNum)}
          >
            {pageNum}
          </button>
        ))}
      </>
    )}
  </div>

  {/* Right: Navigation Arrows */}
  <div className="flex items-center gap-2">
    {/* Previous Button */}
    <button 
      className={`w-[40px] h-[40px] flex items-center justify-center border rounded-full ${
        page === 1 ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white'
      }`}
      onClick={() => page > 1 && handleChangePage(page - 1)}
      disabled={page === 1}
    >
     <img src={ArrowLeft}/>
    </button>

    {/* Next Button */}
    <button 
      className={`w-[40px] h-[40px] flex items-center justify-center border rounded-full ${
        page === maxPage ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white'
      }`}
      onClick={() => page < maxPage && handleChangePage(page + 1)}
      disabled={page === maxPage}
    >
      <img src={ArrowRight}/>
    </button>
  </div>
</div>

          </div>
        </div>
      </div>

      {/* Talent details sidebar */}
      <div
        className={`fixed top-0 right-0 h-full w-[480px] bg-white shadow-lg transform transition-transform duration-300 ease-in-out z-50 ${
          isSidebarOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <TalentDetails 
          onClose={() => setIsSidebarOpen(false)} 
          talent={selectedResult} 
          checkedResults={checkedResults}
          onCheck={handleCheckResult}
        />
      </div>

      {/* Backdrop */}
      {isSidebarOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-40" onClick={() => setIsSidebarOpen(false)} />
      )}

      {/* Add folder modal */}
      {isFolderModalOpen && (
          <Modal outerStyle={"modal_warning"} maxWidth="500px" open={isFolderModalOpen} onClose={() => {if(!isEditModalOpen) setIsFolderModalOpen(false)}} center>
              <div className="p-6">
                  <h1 className="text-xl font-medium mb-4">Add items to folder</h1>
                  <div className="flex border-b mb-4">
                      <p 
                          className={`px-4 py-2 cursor-pointer ${selectedTab === 0 ? 'border-b-2 border-purple-500 font-medium' : ''}`} 
                          onClick={() => setSelectedTab(0)}
                      >
                          Select existing
                      </p>
                      <p 
                          className={`px-4 py-2 cursor-pointer ${selectedTab === 1 ? 'border-b-2 border-purple-500 font-medium' : ''}`} 
                          onClick={() => setSelectedTab(1)}
                      >
                          Create new
                      </p>
                      <p 
                          className={`px-4 py-2 cursor-pointer ${selectedTab === 2 ? 'border-b-2 border-purple-500 font-medium' : ''}`} 
                          onClick={() => setSelectedTab(2)}
                      >
                          Manage folders
                      </p>
                  </div>
                  {selectedTab === 0 ? (
                      <div ref={folderRef} className="mb-4 relative">
                          <div
                              onClick={() => setOpenFolderDropdown(!openFolderDropdown)}
                              className="p-3 border rounded-md flex justify-between items-center cursor-pointer"
                          >
                              <div>
                                  <span className="text-gray-500 block text-sm">Folders</span>
                                  <span className="font-medium">
                                      {selectedFolderDropdown ? selectedFolderDropdown?.folder_name : "Select Folder"}
                                  </span>
                              </div>
                              <LuChevronDown className={`transition-transform ${openFolderDropdown ? 'rotate-180' : ''}`} />
                          </div>
                          {openFolderDropdown && (
                              <div className="absolute left-0 right-0 mt-1 border rounded-md shadow-lg bg-white z-10 max-h-40 overflow-y-auto">
                                  {folders?.map((folder) => (
                                      <div
                                          key={folder._id}
                                          onClick={() => {
                                              setSelectedFolderDropdown(folder);
                                              setOpenFolderDropdown(false); // Close dropdown when folder is selected
                                          }}
                                          className={`p-3 cursor-pointer hover:bg-gray-100 ${selectedFolderDropdown?._id === folder._id ? 'bg-gray-100' : ''}`}
                                      >
                                          {folder.folder_name}
                                      </div>
                                  ))}
                              </div>
                          )}
                      </div>
                  ) : selectedTab === 1 ? (
                      <div className="mb-4">
                          <input
                              type="text"
                              onChange={(event) => { setNewFolder(event.target.value) }}
                              value={newFolder}
                              placeholder="Folder name"
                              className="w-full p-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                          />
                      </div>
                  ) : (
                      <div className="mb-4 max-h-60 overflow-y-auto">
                          {folders.map((folder) => (
                              <div key={folder._id} className="flex justify-between items-center p-3 border-b">
                                  <p className="font-medium">{folder.folder_name}</p>
                                  <div className="flex gap-3">
                                      <button className="text-blue-500 hover:underline" onClick={() => { setEditType("edit"); setEditFolder(folder); setIsEditModalOpen(true); }}>Edit</button>
                                      <button className="text-red-500 hover:underline" onClick={() => { setEditType("delete"); setEditFolder(folder); setIsEditModalOpen(true); }}>Delete</button>
                                  </div>
                              </div>
                          ))}
                          {folders.length === 0 && (
                              <div className="text-center p-4">
                                  <p className="text-gray-500">No folder found</p>
                              </div>
                          )}
                      </div>
                  )}
                  <div className="flex justify-end gap-3 mt-6">
                      <button 
                          className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700" 
                          onClick={() => addToFolder()}
                      >
                          Add
                      </button>
                      <button 
                          className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300" 
                          onClick={() => setIsFolderModalOpen(false)}
                      >
                          Close
                      </button>
                  </div>
              </div>
          </Modal>
      )}

      {/* Edit folder modal */}
      {isEditModalOpen && (
          <Modal outerStyle={"modal_warning"} maxWidth="400px" open={isEditModalOpen} onClose={() => setIsEditModalOpen(false)} center>
              {editType === "delete" ? (
                  <div className="p-6">
                      <div className="text-center mb-6">
                          <h3 className="text-lg font-medium">Are you sure you want to delete this folder?</h3>
                      </div>
                      <div className="flex justify-center gap-3">
                          <button 
                              className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700" 
                              onClick={() => handleDeleteFolder()}
                          >
                              Yes
                          </button>
                          <button 
                              className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300" 
                              onClick={() => setIsEditModalOpen(false)}
                          >
                              No
                          </button>
                      </div>
                  </div>
              ) : (
                  <div className="p-6">
                      <h1 className="text-xl font-medium mb-4">Edit Folder</h1>
                      <div className="mb-4">
                          <input
                              type="text"
                              onChange={(event) => {
                                  setEditFolder({ ...editFolder, folder_name: event.target.value })
                              }}
                              value={editFolder?.folder_name}
                              placeholder="Folder name"
                              className="w-full p-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                          />
                      </div>
                      <div className="flex justify-end gap-3">
                          <button 
                              className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700" 
                              onClick={() => handleUpdateFolder()}
                          >
                              Update
                          </button>
                          <button 
                              className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300" 
                              onClick={() => setIsEditModalOpen(false)}
                          >
                              Close
                          </button>
                      </div>
                  </div>
              )}
          </Modal>
      )}
    </div>
  )
}

function TalentCard({ id, name, title, company, location, flagCode, linkedin, github, onClick, isChecked, onCheck }) {
  // Get profile image based on id
  const profileImage = getProfileImage(id);

  // Get country code from country name
  const countryCode = getCountryCode(flagCode);

  return (
    <div
      className={`border rounded-lg overflow-hidden cursor-pointer bg-[#ffffff]`}
      onClick={onClick}
    >
      <div className="p-3 flex justify-between items-start">
        <div className="flex items-start space-x-2">
          <div className="w-[40px] h-[40px] rounded-full overflow-hidden flex-shrink-0 mt-1">
            <img src={profileImage} alt="" className="w-full h-full object-cover" />
          </div>
          <div className="min-w-0">
            <div className="font-normal text-base">{name}</div>
            <div className="text-sm	font-normal text-gray-400 truncate max-w-[200px]" title={title}>{title}</div>
          </div>
        </div>
        <label 
          className="text-gray-400 flex-shrink-0 cursor-pointer z-10"
          onClick={(e) => e.stopPropagation()}
        >
          <input 
            type="checkbox" 
            className="mr-2 h-4 w-4 cursor-pointer"
            checked={isChecked}
            onChange={(e) => onCheck(id, e)}
          />
        </label>
      </div>
      <div className="px-3 pb-3">
        <div className="flex items-center space-x-1 mb-1">
          <img src={BuildingIcon}/>
          <span className="text-xs truncate font-normal text-sm	">{company}</span>
        </div>
        <div className="flex items-center space-x-1">
          <div className="w-[20px] h-[20px] rounded-full overflow-hidden flex-shrink-0 flex items-center justify-center">
            <ReactCountryFlag
              countryCode={countryCode}
              svg
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover'
              }}
            />
          </div>
          <span className="text-sm font-normal text-gray-500 truncate">{location}</span>
        </div>
      </div>
      <div className="px-3 py-2 flex justify-end">
        <div className="flex space-x-1">
           {linkedin&& <button 
              className="w-5 h-5 rounded-full bg-blue-500 flex items-center justify-center text-white"
              onClick={(e) => {
                if(linkedin){
                  e.stopPropagation()
                  window.open(linkedin, '_blank')
                }
                
              }}
            >
              <img src={Linkedin}/>
            </button>}
         
           {github&& <button 
              className="w-5 h-5 rounded-full bg-gray-800 flex items-center justify-center text-white"
              onClick={(e) => {
                if(github){
                  e.stopPropagation()
                  window.open(github, '_blank')
                }
             
              }}
            >
            <img src={Github}/>
            </button>}
        </div>
      </div>
    </div>
  )
}

export default DashboardNew

